/**
 * Safely parses a value to a number with fallback
 * @param value - The value to parse
 * @param fallback - The fallback value if parsing fails (default: 0)
 * @returns Parsed number or fallback value
 */
export const safeParseNumber = (
  value: string | number | null | undefined,
  fallback: number = 0
): number => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const num = typeof value === 'string' ? Number.parseFloat(value) : value;

  if (Number.isNaN(num) || !Number.isFinite(num)) {
    return fallback;
  }

  return num;
};

interface FormatNumberOptions {
  thousandSeparator?: boolean;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  truncate?: boolean;
}

export const formatNumber = (
  value: string | number | null | undefined,
  {
    thousandSeparator = true,
    maximumFractionDigits = 2,
    minimumFractionDigits = 0,
    truncate = false,
  }: FormatNumberOptions = {}
): string => {
  const num = safeParseNumber(value, 0);
  const prepared = truncate ? Math.trunc(num) : num;

  return new Intl.NumberFormat('en-US', {
    useGrouping: thousandSeparator,
    minimumFractionDigits,
    maximumFractionDigits: truncate ? 0 : maximumFractionDigits,
  }).format(prepared);
};

/**
 * Format an FX rate
 */
export const formatFxRate = (
  rate: string | number | null | undefined
): string =>
  formatNumber(rate, { minimumFractionDigits: 4, maximumFractionDigits: 8 });

/** Format a Date as YYYY-MM-DD for HTML date inputs (uses local timezone) */
export const formatDateForInput = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DAY_MONTH_YEAR: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

// new Date('yyyy-mm-dd') is UTC midnight, which shows as the previous day west of UTC
const parseDate = (date: string): Date => {
  const match = DATE_ONLY.exec(date);
  if (!match) return new Date(date);
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
};

export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? parseDate(date) : date;
  if (Number.isNaN(d.getTime())) return 'Invalid Date';
  return d.toLocaleDateString('nl-NL', DAY_MONTH_YEAR);
};

/**
 * A date as month and year only, e.g. "Jan 2025".
 *
 * For labels that name a period rather than a day - the cap-table matrix's
 * selected range, a metrics period - where a full `formatDate` would imply
 * the day carries meaning it does not. Returns '' for empty input and
 * 'Invalid Date' for unparseable input, on the same terms as `formatDate`.
 */
export const formatMonthYear = (
  date: string | Date | null | undefined
): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return 'Invalid Date';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

export const formatDateTime = (
  date: string | Date | null | undefined
): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return 'Invalid Date';
  return (
    d.toLocaleDateString('nl-NL', DAY_MONTH_YEAR) +
    ' ' +
    d.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
    })
  );
};

/**
 * Format a timestamp as time-only `HH:MM` (24-hour, nl-NL), e.g. "14:30".
 * Used where the date is implied (same day) and only the clock time matters,
 * such as the dashboard's "Last updated: 14:30" label. Returns '' for empty or
 * invalid input.
 */
export const formatTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatRelativeTime = (
  date: string | Date | null | undefined
): string => {
  if (!date) return '';

  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else if (diffWeeks < 4) {
    return `${diffWeeks}w ago`;
  } else if (diffMonths < 12) {
    return `${diffMonths}mo ago`;
  } else {
    return `${diffYears}y ago`;
  }
};

/**
 * Compact shorthand for a number of days, sign-agnostic: "5d", "3w", "4mo",
 * "1.5y". Used for relative due-date labels (e.g. "Due in 3w", "5d overdue").
 * Rolls up to weeks at 14 days, months at 8 weeks, years at 1 year.
 */
export const formatDaySpan = (days: number): string => {
  const magnitude = Math.abs(days);
  if (magnitude < 14) return `${magnitude}d`;
  if (magnitude < 56) return `${Math.round(magnitude / 7)}w`;
  if (magnitude < 365) return `${Math.round(magnitude / 30)}mo`;
  return `${(magnitude / 365).toFixed(1).replace(/\.0$/, '')}y`;
};

interface CurrencyFormatOptions {
  currency?: string | null;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  compact?: boolean;
  currencySign?: Intl.NumberFormatOptions['currencySign'];
}

export const formatCurrency = (
  amount: number | string | null | undefined,
  options?: CurrencyFormatOptions | number
): string => {
  // Handle legacy API where second parameter was maxDigits
  const formatOptions: CurrencyFormatOptions =
    typeof options === 'number'
      ? { maximumFractionDigits: options }
      : options || {};

  const {
    currency: currencyOption,
    locale = 'en-US', // Standardize on en-US for consistency
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    compact = false,
    currencySign = 'standard',
  } = formatOptions;

  const currency = currencyOption?.trim() ? currencyOption : 'EUR';

  const num =
    (typeof amount === 'string' ? Number.parseFloat(amount) : amount) ?? 0;

  if (Number.isNaN(num)) return formatCurrency(0, formatOptions);

  // Handle compact notation for large numbers
  if (compact && Math.abs(num) >= 1000) {
    const absNum = Math.abs(num);
    let suffix = '';
    let divisor = 1;

    if (absNum >= 1_000_000_000) {
      suffix = 'B';
      divisor = 1_000_000_000;
    } else if (absNum >= 1_000_000) {
      suffix = 'M';
      divisor = 1_000_000;
    } else if (absNum >= 1_000) {
      suffix = 'K';
      divisor = 1_000;
    }

    const compactNum = num / divisor;
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencySign,
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });

    const formatted = formatter.format(compactNum);
    // Insert suffix before the last character if it's a space or after currency symbol
    const amount = /^\D*\d[\d.,]*/.exec(formatted)?.[0];
    if (amount) {
      return `${amount}${suffix}${formatted.slice(amount.length)}`;
    }
    return `${formatted}${suffix}`;
  }

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencySign,
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return formatter.format(num);
};

// Specialized currency formatters for common use cases
export const formatCurrencyCompact = (
  amount: number | string | null | undefined,
  currency: string | null = 'EUR'
): string => {
  return formatCurrency(amount, { currency, compact: true });
};

export const formatCurrencyNoDecimals = (
  amount: number | string | null | undefined,
  currency: string | null = 'EUR'
): string => {
  return formatCurrency(amount, {
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const formatCurrencyPrecise = (
  amount: number | string | null | undefined,
  currency: string | null = 'EUR',
  precision: number = 4
): string => {
  return formatCurrency(amount, {
    currency,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

export const formatCurrencyOption = (currency: {
  iso_code: string;
  name: string;
}): string => `${currency.iso_code} - ${currency.name}`;

export const formatPercentage = (
  value: number | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined) return '0.00%';
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Converts percentage string/number to decimal with proper precision handling
 * Fixes floating-point precision issues like 2.2/100 = 0.022000000000000002
 * @param percentage - The percentage value as string or number (e.g., "2.2" or 2.2 for 2.2%)
 * @returns Decimal value rounded to 4 decimal places (e.g., 0.022)
 */
export const percentageToDecimal = (percentage: string | number): number => {
  const num = typeof percentage === 'string' ? Number(percentage) : percentage;
  return Math.round((num / 100) * 10000) / 10000; // Round to 4 decimal places
};

/**
 * Converts decimal to percentage string with proper precision handling
 * Fixes floating-point precision issues like 0.022*100 = 2.1999999999999997
 * @param decimal - The decimal value (e.g., 0.022 for 2.2%)
 * @param decimals - Number of decimal places to show (default: 2)
 * @returns Clean percentage string (e.g., "2.2")
 */
export const decimalToPercentage = (
  decimal: number,
  decimals: number = 2
): string => {
  return (
    Math.round(decimal * 100 * Math.pow(10, decimals)) / Math.pow(10, decimals)
  ).toString();
};

export const formatNumberInput = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Convert to number and back to string to remove leading zeros
  const num = Number.parseInt(digits, 10);
  if (Number.isNaN(num)) return '';

  // Add commas as thousand separators
  return num.toLocaleString('en-US');
};

export const parseNumberInput = (value: string): number => {
  // Remove commas (thousand separators) and parse
  const cleaned = value.replaceAll(',', '');
  const num = Number.parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
};

// Specific formatters for cap table data

export const formatShares = (shares: string | number): string => {
  const num = typeof shares === 'string' ? Number.parseFloat(shares) : shares;
  if (Number.isNaN(num)) return '0';

  // Format with appropriate decimal places
  if (num % 1 === 0) {
    // Whole number, no decimals
    return new Intl.NumberFormat('en-US').format(num);
  } else {
    // Has decimals, show up to 6 decimal places
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    }).format(num);
  }
};

// Utility for parsing form input values safely
export const parseFormNumber = (
  value: string | undefined | null
): number | undefined => {
  if (!value || value.trim() === '') return undefined;
  const cleaned = value.replace(/[$,€,]/g, '');
  const num = Number.parseFloat(cleaned);
  return Number.isNaN(num) ? undefined : num;
};

// Utility for formatting form input values with proper decimal handling
export const formatFormNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return Number(value).toLocaleString('en-US', {
    useGrouping: false,
    maximumFractionDigits: 10,
  });
};

// Format file size in human-readable format
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  );
};

// Format MIME type to human-readable format
export const formatMimeType = (mimeType: string): string => {
  const mimeTypeMap: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/vnd.ms-excel': 'Excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      'Excel',
    'application/msword': 'Word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'Word',
    'image/jpeg': 'JPEG Image',
    'image/png': 'PNG Image',
    'image/gif': 'GIF Image',
    'text/plain': 'Text',
    'text/csv': 'CSV',
    'application/zip': 'ZIP Archive',
    'application/x-zip-compressed': 'ZIP Archive',
  };

  return (
    mimeTypeMap[mimeType] || mimeType.split('/').pop()?.toUpperCase() || 'File'
  );
};

export const formatReferenceType = (
  value: string | null | undefined
): string => {
  if (!value) return '';
  const spaced = value.replaceAll('_', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};
